<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import LoaderAnimation from '@/vue/molecules/loader-animation.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useI18n} from 'vue-i18n';
import {QForm} from 'quasar';
import {useRequiredRule} from '@/ts/composables/pure/use-input-rules';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  renewSuccess: [];
  renewError: [error: string];
}>();

enum RenewalState {
  FORM = 'form',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

const api = useApi();
const {t} = useI18n();
const requiredRule = useRequiredRule();

const renewalState = ref<RenewalState>(RenewalState.FORM);
const voucherCode = ref('');
const error = ref<string | null>(null);
const form = ref<QForm>();

const dialogValue = computed({
  get: () => props.modelValue,
  set: (value: boolean) => { emit('update:modelValue', value); },
});

const closeDialog = (): void => {
  dialogValue.value = false;
  voucherCode.value = '';
  error.value = null;
  renewalState.value = RenewalState.FORM;
};

const processRenewal = async(): Promise<void> => {
  const isValid = await form.value?.validate();
  if (!isValid || !voucherCode.value.trim()) {
    return;
  }

  renewalState.value = RenewalState.PROCESSING;
  error.value = null;

  try {
    await api.me.renewLicense(voucherCode.value.trim());
    renewalState.value = RenewalState.SUCCESS;
    emit('renewSuccess');
  } catch (err) {
    error.value = extractError(err, t);
    renewalState.value = RenewalState.ERROR;
    emit('renewError', error.value);
  }
};

const retryRenewal = (): void => {
  error.value = null;
  renewalState.value = RenewalState.FORM;
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    renewalState.value = RenewalState.FORM;
    voucherCode.value = '';
    error.value = null;
  }
});
</script>

<template>
  <balm-dialog
    v-model="dialogValue"
    type="fit-content"
    theme="grey-10"
    :no-x="renewalState === RenewalState.PROCESSING"
    @update:model-value="closeDialog"
  >
    <div class="renew-voucher-dialog">
      <!-- Form State -->
      <div
        v-if="renewalState === RenewalState.FORM"
        class="renewal-state form-state"
      >
        <div class="form-header">
          <div class="form-icon">
            <svg-illustration name="expired-plan-decoration.svg"/>
          </div>
          <h2 class="form-title">
            {{ $t('renew_voucher_title') }}
          </h2>
          <p class="form-description">
            {{ $t('renew_voucher_description') }}
          </p>
        </div>

        <q-form ref="form" class="renewal-form" @submit.prevent="processRenewal">
          <q-input
            v-model="voucherCode"
            :rules="[requiredRule]"
            :label="$t('voucher_code')"
            class="voucher-input"
          />

          <div class="form-actions">
            <balm-btn
              :label="$t('renew_voucher')"
              theme="blue"
              class="renew-button"
              type="submit"
            />
            <balm-btn
              :label="$t('cancel')"
              theme="grey"
              class="cancel-button"
              @click="closeDialog"
            />
          </div>
        </q-form>
      </div>

      <!-- Processing State -->
      <div
        v-else-if="renewalState === RenewalState.PROCESSING"
        class="renewal-state processing-state"
      >
        <div class="form-icon">
          <svg-illustration name="expired-plan-decoration.svg"/>
        </div>
        <h3 class="processing-title">
          {{ $t('renewing_voucher') }}
        </h3>
        <div class="loading-icon">
          <loader-animation/>
        </div>
        <p class="processing-description">
          {{ $t('please_wait_processing') }}
        </p>
      </div>

      <!-- Success State -->
      <div
        v-else-if="renewalState === RenewalState.SUCCESS"
        class="renewal-state success-state"
      >
        <div class="form-icon">
          <svg-illustration name="high-five.svg"/>
        </div>
        <h2 class="success-title">
          {{ $t('voucher_renewed') }}
        </h2>
        <p class="success-description">
          {{ $t('voucher_renewed_description') }}
        </p>
        <balm-btn
          :label="$t('done')"
          theme="blue"
          class="done-button"
          @click="closeDialog"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="renewalState === RenewalState.ERROR"
        class="renewal-state error-state"
      >
        <div class="state-icon error-icon">
          <svg-illustration name="x-close.svg"/>
        </div>
        <h2 class="state-title error-title">
          {{ $t('renewal_failed') }}
        </h2>
        <p class="state-description error-description">
          {{ error }}
        </p>
        <div class="error-actions">
          <balm-btn
            :label="$t('try_again')"
            theme="blue"
            class="full-width"
            @click="retryRenewal"
          />
          <balm-btn
            :label="$t('cancel')"
            theme="grey"
            class="full-width"
            @click="closeDialog"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.renew-voucher-dialog {
  position: relative;
  overflow: hidden;
  padding: var(--Spacing-spacing-4);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @include screen-md {
    min-width: 460px;
  }
}

.renewal-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 240px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.4s ease-out;
}

.state-icon {
  margin-bottom: var(--Spacing-spacing-5);
  display: flex;
  align-items: center;
  justify-content: center;

  &.error-icon {
    width: 80px;
    height: 80px;
    background: var(--Primary-primary-color-3);
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(203, 109, 82, 0.2);
    animation: errorShake 0.5s ease-out;

    :deep(svg) {
      width: 40px;
      height: 40px;
      fill: white;
    }
  }
}

.state-title {
  @include font-h2;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-100);
  font-weight: 600;

  &.error-title {
    color: var(--Primary-primary-color-3);
  }
}

.state-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 360px;
  line-height: 1.6;

  &.error-description {
    color: var(--Primary-primary-color-3);
    background: rgba(203, 109, 82, 0.05);
    padding: var(--Spacing-spacing-3);
    border-radius: var(--Spacing-spacing-2);
    border: 1px solid rgba(203, 109, 82, 0.1);
  }
}

.form-state {
  align-items: center;
  text-align: center;
  position: relative;
}

.form-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: var(--Spacing-spacing-5);
  padding-bottom: var(--Spacing-spacing-4);
  border-bottom: 2px solid var(--Neutral-UI-neutral-ui-10);
}

.form-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--Spacing-spacing-4);

  :deep(svg),
  :deep(img) {
    max-height: 100px;
    width: auto;
  }
}

.form-title {
  @include font-display-3;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-100);
}

.form-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  max-width: 300px;
}

.renewal-form {
  width: 100%;
}

.voucher-input {
  margin-bottom: var(--Spacing-spacing-5);
  text-align: left;
}

.form-actions,
.error-actions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  flex-direction: column;
  width: 100%;
}

.renew-button,
.cancel-button,
.done-button,
.full-width {
  width: 100%;
}

.processing-state {
  min-height: 300px;
  justify-content: center;
}

.processing-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}

.processing-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
}

.loading-icon {
  transform: scale(0.5);
  margin: calc(-1 * var(--Spacing-spacing-4)) 0;
}

.success-state {
  min-height: 200px;
  justify-content: center;
  padding: var(--Spacing-spacing-4);
}

.success-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-4);
}

.success-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 300px;
  text-align: center;
}

.error-state {
  min-height: 200px;
  justify-content: center;
  width: 100%;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes errorShake {
  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}
</style>
